import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import LanguageIcon from "@mui/icons-material/Language";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  Typography,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Stack
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useAppDispatch } from "../../core/hooks/useStore";
import { useDialog } from "../../hooks/use-dialog";
import { DeleteIntegrationDialog } from "./delete-integration-dialog";

type IntegrationsCardProps = {
  id: string;
  domain: string;
};

const IntegrationsCard = (props: IntegrationsCardProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const deleteIntegrationDialog = useDialog();
  const [connectionStatus, setConnectionStatus] = useState<'untested' | 'testing' | 'connected' | 'error'>('untested');
  const [integrationStatus, setIntegrationStatus] = useState("Click to check connection...");
  const [lastTested, setLastTested] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setConnectionStatus('testing');
      setIntegrationStatus("Testing credentials...");

      const response = await axios.post(`/api/integration/get`, {
        id: props.id,
        validate: true,
      });
      
      const { status, error } = response.data.result.data;
      setLastTested(new Date().toLocaleTimeString());
      
      if (status) {
        setConnectionStatus('connected');
        setIntegrationStatus("WordPress API is working and authorized");
      } else {
        setConnectionStatus('error');
        setIntegrationStatus(error || "Connection failed");
      }
    } catch (error) {
      setConnectionStatus('error');
      setIntegrationStatus("Integration not working");
      setLastTested(new Date().toLocaleTimeString());
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return theme.palette.success.main;
      case 'error': return theme.palette.error.main;
      case 'testing': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      case 'error': return <SyncProblemIcon sx={{ fontSize: 20 }} />;
      case 'testing': return <CircularProgress size={20} />;
      default: return <SyncIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getStatusLabel = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'error': return 'Error';
      case 'testing': return 'Testing...';
      default: return 'Not Tested';
    }
  };

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          border: `1px solid ${alpha(getStatusColor(), 0.2)}`,
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
            borderColor: getStatusColor()
          }
        }}
      >
        {/* WordPress Logo/Icon Section */}
        <Box
          sx={{
            height: 180,
            position: 'relative',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden'
          }}
        >
          {/* Screenshot Preview */}
          <CardMedia
            component="img"
            image={`https://s.wordpress.com/mshots/v1/${props.domain}?w=600`}
            title={props.domain}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.7
            }}
          />
          

          {/* Status Badge */}
          <Chip
            icon={getStatusIcon()}
            label={getStatusLabel()}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              fontWeight: 600,
              bgcolor: alpha(getStatusColor(), 0.1),
              color: getStatusColor(),
              border: `1px solid ${alpha(getStatusColor(), 0.3)}`
            }}
          />
        </Box>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {props.domain}
            </Typography>
            

            <Typography variant="body2" color="text.secondary">
              Your site is connected and ready to receive content from Wordform AI.
            </Typography>
          </Box>

          {/* Connection Status Details */}
          <Box 
            sx={{ 
              mt: 'auto',
              p: 2,
              bgcolor: alpha(getStatusColor(), 0.05),
              borderRadius: 1,
              border: `1px solid ${alpha(getStatusColor(), 0.2)}`
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              CONNECTION STATUS
            </Typography>
            <Typography variant="body2" sx={{ color: getStatusColor(), fontWeight: 600 }}>
              {integrationStatus}
            </Typography>
            {lastTested && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last tested: {lastTested}
              </Typography>
            )}
          </Box>
        </CardContent>

        <Divider />
        
        <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            size="small"
            startIcon={<SyncIcon />}
            onClick={fetchData}
            disabled={connectionStatus === 'testing'}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            {connectionStatus === 'testing' ? 'Testing...' : 'Test Connection'}
          </Button>
          
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={deleteIntegrationDialog.handleOpen}
            color="error"
            sx={{
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.05)
              }
            }}
          >
            Disconnect
          </Button>
        </CardActions>
      </Card>
      
      <DeleteIntegrationDialog
        onClose={deleteIntegrationDialog.handleClose}
        open={deleteIntegrationDialog.open}
        integrationName={props.domain}
        integrationID={props.id}
      />
    </Grid>
  );
};

export default IntegrationsCard;